import React, {useState, useEffect} from 'react'
import './PWAInstallPrompt.css'

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{outcome: 'accepted' | 'dismissed'}>
}

export const PWAInstallPrompt: React.FC = () => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null)
  const [showInstallPrompt, setShowInstallPrompt] = useState(false)
  const [isIOS, setIsIOS] = useState(false)
  const [isStandalone, setIsStandalone] = useState(false)

  const isLocalhost = Boolean(
    window.location.hostname === 'localhost' ||
      window.location.hostname === '[::1]' ||
      window.location.hostname.match(/^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/)
  )

  useEffect(() => {
    // Check if app is already installed (running in standalone mode)
    const isInStandaloneMode = window.matchMedia('(display-mode: standalone)').matches
    setIsStandalone(isInStandaloneMode)
    if (isInStandaloneMode) {
      console.log('PWA: App is in standalone mode')
    }

    // Check if iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent)
    setIsIOS(iOS)

    // Check if user has already dismissed the prompt
    const dismissedAt = localStorage.getItem('pwa-install-dismissed-at')
    const now = Date.now()
    const twentyFourHours = 24 * 60 * 60 * 1000

    let isDismissed = false
    if (dismissedAt) {
      const dismissedTime = parseInt(dismissedAt, 10)
      if (now - dismissedTime < twentyFourHours) {
        isDismissed = true
      }
    }

    if (isDismissed) {
      console.log('PWA: Prompt dismissed recently (checked on mount)')
      setShowInstallPrompt(false)
    } else {
      // Show prompt for iOS by default (beforeinstallprompt doesn't fire on iOS)
      if (iOS && !isInStandaloneMode) {
        setShowInstallPrompt(true)
      }
    }

    // Listen for the beforeinstallprompt event (Android/Desktop)
    const handleBeforeInstallPrompt = (e: Event) => {
      console.log('PWA: beforeinstallprompt event fired')
      e.preventDefault()
      
      // Still need to check if it was dismissed recently inside the event handler
      const dismissedAtInside = localStorage.getItem('pwa-install-dismissed-at')
      let isDismissedInside = false
      if (dismissedAtInside) {
        const dismissedTime = parseInt(dismissedAtInside, 10)
        if (Date.now() - dismissedTime < twentyFourHours) {
          isDismissedInside = true
        }
      }

      if (!isDismissedInside) {
        console.log('PWA: Storing deferred prompt and showing UI')
        setDeferredPrompt(e as BeforeInstallPromptEvent)
        setShowInstallPrompt(true)
      } else {
        console.log('PWA: Prompt dismissed recently, suppressing UI')
      }
    }

    console.log('PWA: Adding beforeinstallprompt listener');
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt)

    return () => {
      console.log('PWA: Removing beforeinstallprompt listener');
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
    }
  }, [])

  const handleInstallClick = async () => {
    if (!deferredPrompt) return

    deferredPrompt.prompt()
    const {outcome} = await deferredPrompt.userChoice

    setDeferredPrompt(null)
    setShowInstallPrompt(false)
  }

  const handleDismiss = () => {
    localStorage.setItem('pwa-install-dismissed-at', Date.now().toString())
    setShowInstallPrompt(false)
  }

  // Don't show if already in standalone mode
  if (isStandalone) {
    return null
  }

  // Don't show if user dismissed it
  if (!showInstallPrompt) {
    return null
  }

  console.log('PWA: Rendering custom install prompt UI');

  // Show iOS install instructions
  if (isIOS && !isStandalone) {
    return (
      <div className='pwa-install-prompt ios-prompt'>
        <div className='pwa-install-content'>
          <div className='pwa-install-header'>
            <h5>Install TrackVAS</h5>
            <button
              onClick={handleDismiss}
              className='btn-close'
              aria-label='Close'
              type='button'
            ></button>
          </div>
          <p className='pwa-install-text'>
            To install this app on your iOS device, tap the share button
            <span className='share-icon'> ⎋ </span>
            and then "Add to Home Screen"
          </p>
        </div>
      </div>
    )
  }

  // Show Android/Desktop install prompt
  if (showInstallPrompt && deferredPrompt) {
    return (
      <div className='pwa-install-prompt'>
        <div className='pwa-install-content'>
          <div className='pwa-install-header'>
            <h5>Install TrackVAS</h5>
            <button
              onClick={handleDismiss}
              className='btn-close'
              aria-label='Close'
              type='button'
            ></button>
          </div>
          <p className='pwa-install-text'>
            Install TrackVAS on your device for a better experience and offline access.
          </p>
          <div className='pwa-install-actions'>
            <button onClick={handleInstallClick} className='btn btn-primary btn-sm'>
              Install App
            </button>
            <button onClick={handleDismiss} className='btn btn-light btn-sm ms-2'>
              Not Now
            </button>
          </div>
        </div>
      </div>
    )
  }

  return null
}